import { FormGroup, Validators } from '@angular/forms';
import { extractBankCode, extractBankName } from '../cashout-bank.helpers';

/** Protocolos SPEI Bitso MXN (POST /v3/withdrawals). */
export const BITSO_MXN_PROTOCOL_OPTIONS: { value: string; label: string }[] = [
  { value: 'clabe', label: 'SPEI - CLABE (cuenta bancaria)' },
  { value: 'debitcard', label: 'SPEI - Tarjeta débito' },
  { value: 'phonenum', label: 'SPEI - Número móvil' },
];

const BITSO_ORIGIN_ID_MAX_LEN = 40;

export function setupMxnCashoutForm(ctx: any): void {
  setDefaultBitsoMxBank(ctx);
  updateBitsoMxnValidators(ctx);

  // MXN usa el grupo bitso_mxn como fuente real de cuenta; el body final
  // rellena account_number con la CLABE/tarjeta/teléfono antes de confirmar.
  const accountNumber = ctx.newTransactionForm.controls.account_number;
  accountNumber.clearValidators();
  accountNumber.updateValueAndValidity({ emitEvent: false });
}

/** Banco simbólico BITSO_MXN para validación add_cashouts (lista `banks.py`). */
export function setDefaultBitsoMxBank(ctx: any): void {
  const banks = ctx.getBanks();
  const selected =
    banks?.find(
      (b: any) => extractBankCode(b)?.toUpperCase() === 'BITSO_MXN',
    ) ?? null;
  if (selected) {
    ctx.newTransactionForm.patchValue({
      company_id: selected,
      company_name: extractBankName(selected) || 'BITSO MXN SPEI',
    });
  } else {
    ctx.newTransactionForm.patchValue({
      company_id: 'BITSO_MXN',
      company_name: 'BITSO MXN SPEI',
    });
  }
}

export function updateBitsoMxnValidators(ctx: any): void {
  const g = ctx.newTransactionForm.get('bitso_mxn') as FormGroup | null;
  if (!g) return;

  g.get('protocol')?.setValidators([Validators.required]);
  g.get('clabe')?.setValidators([Validators.required]);
  const proto = getBitsoMxProtocolValue(ctx);
  const inst = g.get('institution_code');
  if (proto === 'debitcard' || proto === 'phonenum') {
    inst?.setValidators([Validators.required]);
  } else {
    inst?.clearValidators();
  }
  g.get('protocol')?.updateValueAndValidity({ emitEvent: false });
  g.get('clabe')?.updateValueAndValidity({ emitEvent: false });
  inst?.updateValueAndValidity({ emitEvent: false });
}

export function getBitsoMxClabeLabel(ctx: any): string {
  const p = getBitsoMxProtocolValue(ctx);
  if (p === 'debitcard') return 'Número de tarjeta débito';
  if (p === 'phonenum') return 'Número móvil (10 dígitos típ.)';
  return 'CLABE (18 dígitos)';
}

export function getBitsoMxClabePlaceholder(ctx: any): string {
  const p = getBitsoMxProtocolValue(ctx);
  if (p === 'debitcard') return '16 dígitos de la tarjeta';
  if (p === 'phonenum') return 'Ej. 8180833954';
  return '18 dígitos CLABE';
}

/**
 * Payload según Bitso MXN withdrawals (currency, protocol, beneficiary, clabe,
 * rfc, refs opcionales y origin_id compatible con Bitso).
 */
export function buildBitsoMxnWithdrawalPayload(
  ctx: any,
): Record<string, unknown> | null {
  const mx = ctx.newTransactionForm.get('bitso_mxn') as FormGroup;
  const v = mx.getRawValue() as Record<string, string>;
  const first = (ctx.newTransactionForm.controls.firstname.value ?? '')
    .toString()
    .trim();
  const last = (ctx.newTransactionForm.controls.lastname.value ?? '')
    .toString()
    .trim();
  const beneficiary = [first, last].filter(Boolean).join(' ').trim();
  if (!beneficiary) return null;

  const amountRaw = ctx.newTransactionForm.controls.amount.value;
  const amountNum =
    typeof amountRaw === 'string' ? parseFloat(amountRaw) : Number(amountRaw);
  if (!Number.isFinite(amountNum) || amountNum <= 0) return null;

  const origin_id = buildBitsoMxnOriginId(ctx);
  if (!origin_id) return null;

  const protocol = (v['protocol'] ?? 'clabe').toString().trim().toLowerCase();
  const clabe = (v['clabe'] ?? '').toString().trim();
  if (!clabe) return null;

  const payload: Record<string, unknown> = {
    currency: 'mxn',
    protocol,
    amount: String(amountNum),
    beneficiary,
    clabe,
    origin_id,
  };

  const rfc = (v['rfc'] ?? '').toString().trim();
  if (rfc) payload['rfc'] = rfc.toUpperCase();

  const notes = (v['notes_ref'] ?? '').toString().trim().slice(0, 40);
  if (notes) payload['notes_ref'] = notes;

  const institutionCode = (v['institution_code'] ?? '').toString().trim();
  if (
    institutionCode &&
    (protocol === 'debitcard' || protocol === 'phonenum')
  ) {
    payload['institution_code'] = institutionCode;
  }

  return payload;
}

function getBitsoMxProtocolValue(ctx: any): string {
  const g = ctx.newTransactionForm.get('bitso_mxn') as FormGroup | null;
  return (g?.get('protocol')?.value ?? 'clabe') as string;
}

/**
 * `origin_id` para Bitso: [a-zA-Z0-9_], sin `-`, máx. 40 caracteres.
 * Base36 en el sufijo aleatorio solo usa 0-9a-z, que Bitso acepta.
 */
function buildBitsoMxnOriginId(ctx: any): string {
  const tid = ctx.newTransactionForm.controls.transaction_id.value;
  const raw = tid
    ? `TS_mx_${tid}`
    : `TS_mx_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  let out = raw.replace(/[^a-zA-Z0-9_]/g, '').slice(0, BITSO_ORIGIN_ID_MAX_LEN);
  if (!out) {
    out = `TS_mx_${Date.now()}`
      .replace(/[^a-zA-Z0-9_]/g, '')
      .slice(0, BITSO_ORIGIN_ID_MAX_LEN);
  }
  return out;
}
